import { useCallback } from "react";

import useTypedContext from "hooks/useTypedContext";
import useSelfServiceFlags from "hooks/useSelfServiceFlags";
import { Stack } from "types/generated";
import { AccessResult, hasAdminAccess, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountContext } from "views/AccountWrapper";

import { checkTemplateDeploymentAccess } from "./helpers/checkTemplateDeploymentAccess";

type StackForAdminAccess = Pick<Stack, "spaceDetails" | "space"> &
  Partial<Pick<Stack, "blueprintDeployment">>;

/**
 * Hook that returns a function to check if the current user has admin access to a stack.
 * Stacks created from a template deployment (blueprintDeployment) cannot be managed.
 */
const useHasStackAdminAccess = (): ((stack?: StackForAdminAccess) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const { enableSelfServiceV2 } = useSelfServiceFlags();

  return useCallback(
    (stack) => {
      if (!stack) {
        return { result: false };
      }

      const blueprintCheck = checkTemplateDeploymentAccess(enableSelfServiceV2, stack);
      if (blueprintCheck) {
        return blueprintCheck;
      }

      if (viewer.admin) {
        return { result: true };
      }

      const hasAccess = hasAdminAccess(viewer, stack.spaceDetails?.id || stack.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, enableSelfServiceV2]
  );
};

export default useHasStackAdminAccess;
